import { useCallback, useMemo, useRef } from 'react';
import { isSameDay } from 'date-fns';

import { useTranslation } from '@kc-console/core';

import { CALENDAR_MAX_DATE, CALENDAR_MIN_DATE } from '../constants';
import DatePickerContainer from '../core/DatePickerContainer';
import DatePickerHeader from '../core/DatePickerHeader';
import PeriodPickerHeader from '../core/PeriodPickerHeader';

import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import type { Locale } from 'date-fns';
import type { CalendarProps, DatePickerContainerProps, PeriodPickerKind } from '../types';

interface UseCalendarPropsOptions {
  dateLocale: Locale;
  disabled: boolean;
  finalShowTime: boolean;
  handleTimeChange: (time: Date | null) => void;
  inline: boolean;
  locale: 'ko' | 'en';
  monthsShown: number;
  props: CalendarProps;
  selectsRange: boolean;
  showSeconds: boolean;
  showTimezone: boolean;
  calendarView?: PeriodPickerKind;
  canonicalizeDate?: (date: Date | null) => Date | null;
}

const useCallbackRef = <TArgs extends unknown[], TResult>(
  callback: (...args: TArgs) => TResult,
) => {
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  return useCallback((...args: TArgs) => callbackRef.current(...args), []);
};

/**
 * ReactDatePicker에 전달할 props를 생성하는 커스텀 훅
 *
 * @description
 * - 공통 DatePicker props 설정
 * - 타입별(single/range) props 분기 처리
 * - DatePickerContainer, DatePickerHeader 설정
 * - useMemo로 불필요한 재생성 방지
 *
 * @param options - Calendar props 생성 옵션
 * @returns ReactDatePicker에 전달할 props 객체
 */
export const useCalendarProps = (options: UseCalendarPropsOptions) => {
  const {
    props,
    dateLocale,
    locale,
    finalShowTime,
    showSeconds,
    showTimezone,
    monthsShown,
    selectsRange,
    inline,
    handleTimeChange,
    calendarView,
    canonicalizeDate,
    disabled,
  } = options;
  const { t } = useTranslation();

  const calendarContainer = useCallbackRef((containerProps: DatePickerContainerProps) => (
    <DatePickerContainer
      {...containerProps}
      showTime={finalShowTime}
      showSeconds={showSeconds}
      showTimezone={showTimezone}
      timeValue={props.selected}
      onTimeChange={handleTimeChange}
      monthsShown={monthsShown}
      name={props.name}
      inline={inline}
      minuteInterval={props.minuteInterval}
      hourInterval={props.hourInterval}
      secondInterval={props.secondInterval}
      hideRangeHighlight={!!(props.selectsStart || props.selectsEnd)}
    />
  ));

  const renderCustomHeader = useCallback(
    (headerProps: ReactDatePickerCustomHeaderProps) => {
      if (calendarView) {
        return <PeriodPickerHeader {...headerProps} calendarView={calendarView} locale={locale} />;
      }

      return <DatePickerHeader {...headerProps} locale={locale} monthsShown={monthsShown} />;
    },
    [calendarView, locale, monthsShown],
  );

  return useMemo(() => {
    const hasRangeContext = selectsRange || !!props.selectsStart || !!props.selectsEnd;
    const minDate = canonicalizeDate
      ? (canonicalizeDate(props.minDate ?? CALENDAR_MIN_DATE) ?? CALENDAR_MIN_DATE)
      : (props.minDate ?? CALENDAR_MIN_DATE);
    const maxDate = canonicalizeDate
      ? (canonicalizeDate(props.maxDate ?? CALENDAR_MAX_DATE) ?? CALENDAR_MAX_DATE)
      : (props.maxDate ?? CALENDAR_MAX_DATE);
    const excludeDates = canonicalizeDate
      ? props.excludeDates?.filter(date => {
          const canonicalDate = canonicalizeDate(date);
          return canonicalDate ? isSameDay(date, canonicalDate) : false;
        })
      : props.excludeDates;
    const commonDatePickerProps = {
      dayClassName: calendarView
        ? undefined
        : (date: Date) =>
            selectsRange && props.startDate && !props.endDate && isSameDay(date, props.startDate)
              ? 'kc-datepicker__day--range-anchor'
              : '',
      disabled,
      excludeDates,
      filterDate: calendarView && disabled ? () => false : undefined,
      inline: true,
      locale: dateLocale,
      maxDate,
      minDate,
      monthsShown,
      selectsMultiple: undefined,
      selectsStart: props.selectsStart,
      selectsEnd: props.selectsEnd,
      showPopperArrow: false,
      showToday: props.showToday,
      startOfWeek: props.startOfWeek,
      calendarContainer,
      renderCustomHeader,
    };

    const periodDatePickerProps =
      calendarView === 'year'
        ? { showYearPicker: true, yearItemNumber: 12 }
        : calendarView === 'year-month'
          ? { showFourColumnMonthYearPicker: true, showMonthYearPicker: true }
          : calendarView === 'quarter'
            ? {
                renderQuarterContent: (quarter: number) =>
                  t('ui:datepicker.period.quarter_label', { quarter }),
                showQuarterYearPicker: true,
              }
            : {};

    const typeSpecificProps = selectsRange
      ? {
          endDate: props.endDate,
          selected: undefined,
          startDate: props.startDate,
        }
      : {
          endDate: hasRangeContext ? props.endDate : undefined,
          selected: props.selected,
          startDate: hasRangeContext ? props.startDate : undefined,
        };

    return {
      ...commonDatePickerProps,
      ...periodDatePickerProps,
      ...typeSpecificProps,
    };
  }, [
    props.excludeDates,
    props.maxDate,
    props.minDate,
    props.showToday,
    props.startOfWeek,
    props.selected,
    props.endDate,
    props.startDate,
    props.selectsStart,
    props.selectsEnd,
    calendarView,
    canonicalizeDate,
    disabled,
    dateLocale,
    calendarContainer,
    renderCustomHeader,
    monthsShown,
    selectsRange,
    t,
  ]);
};
